import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { AdminSidebar } from '../components/AdminSidebar';
import { useNavigate } from 'react-router-dom';

import { exportToExcel, exportToPDF } from '../utils/exportUtils';
export default function AdminMasterView() {
    const { api, logout } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    // FY Options for Profile / Settings
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const baseYear = 2023;
    const maxYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    const fyOptions = [];
    for (let y = baseYear; y <= maxYear + 1; y++) {
        fyOptions.push(`FY ${y}-${y + 1}`);
    }

    const defaultFy = currentMonth >= 3 ? `FY ${currentYear}-${currentYear + 1}` : `FY ${currentYear - 1}-${currentYear}`;
    const [fy, setFy] = useState(defaultFy);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const res = await api.get('/api/admin/clients');
                setClients(res.data);
            } catch (err) {
                toast.error("Failed to load clients");
            }
        };
        fetchClients();
    }, [api]);

    useEffect(() => {
        if (!selectedClient) {
            setTransactions([]);
            return;
        }

        const fetchTenantTransactions = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/api/admin/tenant/${selectedClient}/transactions`);
                setTransactions(res.data);
            } catch (err) {
                toast.error("Failed to load transactions for tenant");
            } finally {
                setLoading(false);
            }
        };
        fetchTenantTransactions();
    }, [selectedClient, api]);

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);
    };
    const filteredTransactions = transactions.filter(t => {
        if (!t.date) return false;
        const tDate = new Date(t.date);
        
        // Custom Date Range
        if (startDate && new Date(startDate) > tDate) return false;
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (end < tDate) return false;
        }

        // Month Filter
        if (selectedMonth !== '') {
            if (tDate.getMonth() !== parseInt(selectedMonth)) return false;
        }

        // FY Filter (April to March)
        if (fy) {
            const parts = fy.replace('FY ', '').split('-');
            if (parts.length === 2) {
                const startYear = parseInt(parts[0]);
                const endYear = parseInt(parts[1]);
                const fyStart = new Date(startYear, 3, 1); // April 1st
                const fyEnd = new Date(endYear, 2, 31, 23, 59, 59); // March 31st
                if (tDate < fyStart || tDate > fyEnd) return false;
            }
        }

        return true;
    });

    const exportColumns = [
        { header: 'Date', dataKey: (row) => row.date ? new Date(row.date).toLocaleDateString() : '-' },
        { header: 'Type', dataKey: (row) => (row.type || '').toUpperCase() },
        { header: 'Category', dataKey: (row) => row.category || '-' },
        { header: 'Amount', dataKey: 'amount' }
    ];

    const handleExportExcel = () => {
        exportToExcel(filteredTransactions, `${selectedClient}_master_ledger`, exportColumns);
        toast.success("Excel exported successfully!");
    };

    const handleExportPDF = () => {
        exportToPDF(filteredTransactions, `${selectedClient}_master_ledger`, exportColumns, `Master Ledger - ${selectedClient}`);
        toast.success("PDF exported successfully!");
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="h-16 bg-[#0A192F] text-white flex items-center justify-between px-6 border-b border-[#EAB308]">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#EAB308] rounded text-[#0A192F] font-bold flex items-center justify-center">RC</div>
                    <span className="font-['Cabinet_Grotesk'] font-bold tracking-wide">Super Admin Panel</span>
                </div>
                <button onClick={async () => { await logout(); navigate('/'); }} className="text-sm text-slate-300 hover:text-white transition-colors">Sign Out</button>
            </header>

            <div className="flex">
                <AdminSidebar />

                <main className="flex-1 p-6 max-w-7xl space-y-8">
                    <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div>
                            <h1 className="text-2xl font-bold text-[#0F284E]">Master Ledger View</h1>
                            <p className="text-slate-500 text-sm mt-1">Select a client tenant to oversee all their transactions.</p>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 items-end">
                        <div className="flex-1 w-full">
                            <label className="block text-sm font-semibold text-[#0F284E] mb-2">Select Client Tenant</label>
                            <select 
                                data-testid="admin-master-client-select"
                                value={selectedClient} 
                                onChange={(e) => setSelectedClient(e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]"
                            >
                                <option value="">-- Select a Client --</option>
                                {clients.map(client => (
                                    <option key={client._id} value={client.client_slug}>
                                        {client.business_name} ({client.client_slug})
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="flex-1 w-full">
                             <label className="block text-sm font-semibold text-[#0F284E] mb-2">Financial Year</label>
                             <select 
                                data-testid="admin-master-fy-select"
                                value={fy} 
                                onChange={e => setFy(e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]"
                             >
                                 {fyOptions.map(option => (
                                     <option key={option} value={option}>{option}</option>
                                 ))}
                             </select>
                        </div>

                        <div className="flex-1 w-full">
                            <label className="block text-sm font-semibold text-[#0F284E] mb-2">Month</label>
                            <select 
                                data-testid="admin-master-month-select"
                                value={selectedMonth} 
                                onChange={e => setSelectedMonth(e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]"
                            >
                                <option value="">-- All Months --</option>
                                <option value="0">January</option>
                                <option value="1">February</option>
                                <option value="2">March</option>
                                <option value="3">April</option>
                                <option value="4">May</option>
                                <option value="5">June</option>
                                <option value="6">July</option>
                                <option value="7">August</option>
                                <option value="8">September</option>
                                <option value="9">October</option>
                                <option value="10">November</option>
                                <option value="11">December</option>
                            </select>
                        </div>
                        
                        <div className="flex-1 w-full">
                            <label className="block text-sm font-semibold text-[#0F284E] mb-2">Custom Range</label>
                            <div className="flex gap-2">
                                <input data-testid="admin-master-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full h-10 px-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                <span className="self-center">-</span>
                                <input data-testid="admin-master-end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full h-10 px-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                            </div>
                        </div>
                    </div>

                    {selectedClient && (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                                <h3 className="font-bold text-[#0F284E]">Tenant Transactions</h3>
                                <div className="flex gap-2">
                                    <button 
                                        data-testid="admin-master-export-excel"
                                        onClick={handleExportExcel}
                                        className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded transition-colors font-medium border border-emerald-200"
                                    >
                                        Export Excel
                                    </button>
                                    <button 
                                        data-testid="admin-master-export-pdf"
                                        onClick={handleExportPDF}
                                        className="text-xs bg-rose-50 hover:bg-rose-100 text-rose-700 px-3 py-1.5 rounded transition-colors font-medium border border-rose-200"
                                    >
                                        Export PDF
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-mono tracking-wider text-slate-600">
                                        <tr>
                                            <th className="px-6 py-4">Date</th>
                                            <th className="px-6 py-4">Type</th>
                                            <th className="px-6 py-4">Category</th>
                                            <th className="px-6 py-4">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {loading ? (
                                            <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-500">Loading transactions...</td></tr>
                                        ) : filteredTransactions.length === 0 ? (
                                            <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-500">No transactions found for this tenant.</td></tr>
                                        ) : filteredTransactions.map(tx => (
                                            <tr key={tx._id} className="hover:bg-slate-50/50">
                                                <td className="px-6 py-4 font-mono text-slate-600">
                                                    {tx.date ? new Date(tx.date).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                                                        tx.type === 'income' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'
                                                    }`}>
                                                        {tx.type.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-slate-600">{tx.category || '-'}</td>
                                                <td className="px-6 py-4 font-bold text-[#0F284E]">{formatCurrency(tx.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}